import React, { useEffect, useState } from 'react';
import {
   filter,
   switchMap,
   takeUntil,
   distinctUntilChanged,
   map,
   distinctUntilKeyChanged,
   throttleTime,
   tap,
} from 'rxjs/operators';
import { merge, of, animationFrameScheduler, fromEvent } from 'rxjs';

import { SliderDisplay } from './SliderDisplay';
import { leftClickEventFilter } from '../util/leftClickEventFilter';
import { calculateParameterValue } from './calculateParameterValue';

const Slider = ({
   value,
   x,
   y,
   width,
   height,
   trackWidth,
   handleHeight,
   incrementCount,
   bipolar,
   onChange,
   disabled,
   ghost,
   notApplicable,
   tested,
   correct,
   highlighted,
   suggestedValue,
}) => {
   const sliderRef = React.useRef(null);
   const [isActive, setIsActive] = useState(false);

   useEffect(() => {
      if (disabled || notApplicable) {
         // no clean up function needed, bc no event listener is added
         return undefined;
      }
      const mousedown$ = fromEvent(sliderRef.current, 'pointerdown')
         .pipe(
            filter(leftClickEventFilter),
            tap(() => setIsActive(true)),
            map((e) => {
               const { top, bottom } = e.currentTarget.getBoundingClientRect();
               return {
                  bounds: {
                     top: top + handleHeight / 2,
                     bottom: bottom - handleHeight / 2,
                  },
                  clientY: e.clientY,
               };
            }),
            switchMap(({ bounds, clientY }) =>
               merge(of({ clientY }), fromEvent(document, 'pointermove')).pipe(
                  throttleTime(0, animationFrameScheduler),
                  distinctUntilKeyChanged('clientY'),
                  map(({ clientY: _y }) =>
                     Math.min(Math.max(bounds.top, _y), bounds.bottom)
                  ),
                  distinctUntilChanged(),
                  map((_y) =>
                     calculateParameterValue({
                        y: _y,
                        bounds,
                        bipolar,
                        incrementCount,
                     })
                  ),
                  distinctUntilChanged(),
                  tap(onChange),
                  takeUntil(
                     merge(
                        fromEvent(document, 'pointerup'),
                        fromEvent(document, 'pointercancel'),
                        fromEvent(document, 'tutorial-cancel-user-action')
                     ).pipe(
                        filter(leftClickEventFilter),
                        tap(() => setIsActive(false))
                     )
                  )
               )
            )
         )
         .subscribe();

      return () => mousedown$.unsubscribe();
   }, [incrementCount, disabled, notApplicable]);

   return (
      <SliderDisplay
         ref={sliderRef}
         value={value}
         x={x}
         y={y}
         width={width}
         height={height}
         trackWidth={trackWidth}
         handleHeight={handleHeight}
         bipolar={bipolar}
         isActive={isActive}
         disabled={disabled}
         ghost={ghost}
         notApplicable={notApplicable}
         tested={tested}
         correct={correct}
         highlighted={highlighted}
         suggestedValue={suggestedValue}
      />
   );
};

export { Slider };
